import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { User } from '@vsolv/core/users/domain';
import { UserService } from '@vsolv/core/users/web';
import { PropertyListener } from '@vsolv/dev-kit/rx';
import { GatewayInstance, Payment, PaymentTax } from '@vsolv/packages/payments/domain';
import {
  PayLaterPlanOption,
  PaymentService,
  paymentGatewayName,
  paymentGatewayProvider,
  paymentGatewayProvenance,
} from '@vsolv/packages/payments/web';
import { PortalService } from '@vsolv/packages/portal-config/web';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { Staff } from '@wsphere/staff/domain';
import { SecurityService } from '@wsphere/staff/web';
import { Warranty } from '@wsphere/warranties/domain';
import { WarrantyWebService } from '../../../warranty/services';
import { BehaviorSubject, combineLatest, from, map, ReplaySubject, switchMap } from 'rxjs';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-payment-invoice',
  templateUrl: './payment-invoice.component.html',
  standalone: false,
})
export class PaymentInvoiceComponent {
  protected readonly paymentGatewayProvider = paymentGatewayProvider;
  constructor(
    private userSvc: UserService,
    private route: ActivatedRoute,
    private toastSvc: ToastService,
    private portalSvc: PortalService,
    private paymentSvc: PaymentService,
    private securitySvc: SecurityService,
    private warrantySvc: WarrantyWebService
  ) {}

  @Output() readonly refresh = new EventEmitter<void>();

  @Input() static = false;
  @Input() hideResendReminderButton = false;
  /** Explicit staff-only opt-in. Customer portal and static receipt consumers leave this false. */
  @Input() showGatewayAssignment = false;

  protected readonly logo = this.portalSvc.config.logo;
  protected readonly brandName = this.portalSvc.config.brandName;
  protected readonly contactEmail = this.portalSvc.config.contactEmail;
  protected readonly contactPhone = this.portalSvc.config.contactPhone;
  protected readonly contactAddress = this.portalSvc.config.contactAddress;

  @PropertyListener('payment') payment$ = new ReplaySubject<Payment.Model | null>(1);
  @Input() payment?: Payment.Model & { user: User.Model; taxes: PaymentTax.Model[] };

  @PropertyListener('warranty') private warrantyContext$ = new BehaviorSubject<Warranty.Model | null>(null);
  @Input() warranty: Warranty.Model | null = null;

  @Input() showPaymentMethod = false;
  @PropertyListener('showPaymentMethod') showPaymentMethod$ = new BehaviorSubject<boolean>(this.showPaymentMethod);

  get isSameAddress() {
    if (!this.payment?.shippingAddress || !this.payment.paymentMethod?.billingAddress) return false;
    return (
      this.payment.shippingAddress.line1 === this.payment.paymentMethod.billingAddress.line1 &&
      this.payment.shippingAddress.city === this.payment.paymentMethod.billingAddress.city &&
      this.payment.shippingAddress.state === this.payment.paymentMethod.billingAddress.state &&
      this.payment.shippingAddress.zip === this.payment.paymentMethod.billingAddress.zip
    );
  }

  get showInternalGatewayAssignment() {
    return this.showGatewayAssignment && !this.static;
  }

  protected get gatewayNamePresentation() {
    const gateway = this.payment?.gateway;
    const isCurrent = gateway?.assignmentState === 'assigned' && gateway.labelSource === 'current_instance';
    return {
      label: isCurrent ? 'Gateway name (current)' : 'Gateway name',
      name: paymentGatewayName(gateway),
      note: isCurrent ? 'Original name unavailable.' : paymentGatewayProvenance(gateway),
    };
  }

  protected gatewayRoutingReasonLabel(reason: Payment.Model['gatewayRoutingReason']): string | null {
    switch (reason) {
      case GatewayInstance.RoutingReason.POLICY_DISTRIBUTOR_RULE:
        return 'Policy + distributor rule';
      case GatewayInstance.RoutingReason.DISTRIBUTOR_RULE:
        return 'Distributor rule';
      case GatewayInstance.RoutingReason.POLICY_RULE:
        return 'Policy rule';
      case GatewayInstance.RoutingReason.PLATFORM_SHARED_AUTHNET:
        return 'Platform shared Authorize.Net';
      case GatewayInstance.RoutingReason.TENANT_DEFAULT:
        return 'Tenant default';
      case GatewayInstance.RoutingReason.BATCH_UNANIMOUS:
        return 'Batch unanimous';
      case GatewayInstance.RoutingReason.LEGACY_COMPATIBILITY:
        return 'Legacy compatibility';
      case GatewayInstance.RoutingReason.RENEWAL_AFFINITY:
        return 'Renewal affinity';
      default:
        return null;
    }
  }

  currentUser$ = from(this.userSvc.getSelf());

  warranty$ = combineLatest([this.payment$, this.warrantyContext$]).pipe(
    switchMap(
      async ([payment, warranty]) =>
        warranty ?? (payment?.referenceId ? await this.warrantySvc.getOneByContractNumber(payment.referenceId) : null)
    )
  );

  distributor$ = this.warranty$.pipe(map(warranty => warranty?.distributor));

  currentStaff$ = combineLatest(
    (this.route.pathFromRoot ?? [this.route.parent?.parent ?? this.route]).map(route => route.data)
  ).pipe(map(data => [...data].reverse().find(item => item['staff'])?.['staff'] as Staff.Model | undefined));

  canRetryBillingInfo$ = combineLatest([this.payment$, this.currentUser$, this.currentStaff$, this.distributor$]).pipe(
    switchMap(async ([payment, user, staff, dist]) => {
      if (payment?.userId === user.id) return true;
      return staff ? await this.securitySvc.hasAccess('py_RetryPayment', dist ? [dist.permissionKey] : null) : null;
    })
  );

  canViewPaymentMethods$ = combineLatest([this.currentStaff$, this.showPaymentMethod$]).pipe(
    switchMap(async ([staff, showPaymentMethod]) =>
      !showPaymentMethod
        ? staff
          ? await this.securitySvc.hasAccess(
              'py_ViewPaymentMethods',
              this.securitySvc.globalDistributors$.value?.map(dist => dist.permissionKey) || null
            )
          : null
        : showPaymentMethod
    )
  );

  canSettleExternal$ = combineLatest([this.payment$, this.currentStaff$, this.distributor$]).pipe(
    switchMap(
      async ([payment, staff, distributor]) =>
        !!staff &&
        !!payment &&
        Payment.canSettleExternally(payment) &&
        (await this.securitySvc.hasAccess('wrt_UpdateWarranty', distributor ? [distributor.permissionKey] : null))
    )
  );

  showReceiptForm = false;
  settling = false;
  receiptAttachment: File | null = null;
  private uploadedReceiptAttachmentId?: string;
  private uploadedReceiptAttachmentFile?: File;

  selectReceiptAttachment(event: Event) {
    this.receiptAttachment = (event.target as HTMLInputElement).files?.[0] ?? null;
    this.uploadedReceiptAttachmentId = undefined;
    this.uploadedReceiptAttachmentFile = undefined;
  }

  async downloadReceiptAttachment() {
    if (!this.payment) return;
    try {
      const { url } = await this.paymentSvc.receiptAttachmentUrl(this.payment.id);
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.click();
    } catch {
      this.toastSvc.show({ type: 'error', title: 'Unable to open proof of receipt', text: 'Please try again.' });
    }
  }
  externalChannels = Object.values(Payment.ExternalPaymentChannel);
  receiptForm = new FormGroup({
    currency: new FormControl('USD', { nonNullable: true, validators: [Validators.required] }),
    receivedDate: new FormControl(new Date().toLocaleDateString('en-CA'), {
      nonNullable: true,
      validators: [Validators.required],
    }),
    channel: new FormControl(Payment.ExternalPaymentChannel.CHECK, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    reference: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(255)] }),
    note: new FormControl('', { nonNullable: true, validators: [Validators.maxLength(2000)] }),
  });

  async recordExternalReceipt() {
    if (!this.payment || this.receiptForm.invalid || this.settling) return;
    this.settling = true;
    try {
      if (this.receiptAttachment && this.uploadedReceiptAttachmentFile !== this.receiptAttachment) {
        const uploaded = await this.paymentSvc.uploadReceiptAttachment(this.payment.id, this.receiptAttachment);
        this.uploadedReceiptAttachmentId = uploaded.id;
        this.uploadedReceiptAttachmentFile = this.receiptAttachment;
      }
      await this.paymentSvc.settleExternalPayment(this.payment.id, {
        ...this.receiptForm.getRawValue(),
        amount: this.payment.amount,
        attachmentStorageItemId: this.uploadedReceiptAttachmentId,
      });
      this.showReceiptForm = false;
      this.refresh.emit();
      this.toastSvc.show({
        type: 'success',
        title: 'External receipt recorded',
        text: `The ${this.payment.collectionMode === Payment.CollectionMode.PAY_LATER ? 'balance' : 'installment'} is fully settled.`,
      });
    } catch (error) {
      const message = (error as { error?: { message?: string } }).error?.message;
      this.toastSvc.show({
        type: 'error',
        title: 'Unable to record receipt',
        text: typeof message === 'string' ? message : 'Receipt was not recorded. Refresh the payment and try again.',
      });
    } finally {
      this.settling = false;
    }
  }

  // Pay Later balance on one of the term's installment plans (VS-1458).
  payLaterPlans: PayLaterPlanOption[] = [];
  selectedPlan: PayLaterPlanOption | null = null;
  planPaymentMethod = new FormControl<string | null>(null, Validators.required);
  /** Staff only: settle the first installment with the receipt form instead of charging it. */
  planFirstByReceipt = false;
  payingPlan = false;

  /**
   * Pay in full keeps today's flow. When the balance's term also has installment plans, the payer
   * picks one first, the same choices checkout offers.
   */
  async openPayLater(payInFull: { open(): void }, choosePlan: { open(): void }) {
    if (!this.payment) return;
    try {
      const { options } = await this.paymentSvc.listPayLaterPlans(this.payment.id);
      this.payLaterPlans = options;
    } catch {
      this.payLaterPlans = [];
    }
    if (!this.payLaterPlans.some(option => option.installments > 1)) {
      payInFull.open();
      return;
    }
    this.selectedPlan = null;
    this.planFirstByReceipt = false;
    this.planPaymentMethod.reset(null);
    choosePlan.open();
  }

  choosePayLaterPlan(option: PayLaterPlanOption, payInFull: { open(): void }, choosePlan: { close(): void }) {
    if (option.installments > 1) {
      this.selectedPlan = option;
      return;
    }
    choosePlan.close();
    payInFull.open();
  }

  async payOnPlan(choosePlan: { close(): void }) {
    const plan = this.selectedPlan;
    const paymentMethodId = this.planPaymentMethod.value;
    if (!this.payment || !plan || !paymentMethodId || this.payingPlan) return;
    if (this.planFirstByReceipt && this.receiptForm.invalid) return;

    this.payingPlan = true;
    try {
      await this.paymentSvc.payLaterInInstallments(this.payment.id, {
        scheduleId: plan.scheduleId,
        paymentMethodId,
        firstInstallmentReceipt: this.planFirstByReceipt
          ? { ...this.receiptForm.getRawValue(), amount: plan.installmentAmount }
          : undefined,
      });
      choosePlan.close();
      this.refresh.emit();
      this.toastSvc.show({
        type: 'success',
        title: 'Payment plan started',
        text: `The first of ${plan.installments} installments is paid; the rest are scheduled.`,
      });
    } catch (error) {
      const message = (error as { error?: { message?: string } }).error?.message;
      this.toastSvc.show({
        type: 'error',
        title: 'Unable to start the payment plan',
        text: typeof message === 'string' ? message : 'Refresh the payment and check its status before trying again.',
      });
      this.refresh.emit();
    } finally {
      this.payingPlan = false;
    }
  }

  protected async resendPaymentReminder() {
    if (!this.payment) {
      this.toastSvc.show({
        title: 'Error Resending Payment Reminder',
        type: 'error',
        text: 'Unknown Error',
      });
      return;
    }
    await this.paymentSvc.resendPaymentReminder(this.payment.id);
    this.toastSvc.show({
      type: 'success',
      title: 'Payment Reminder Resent',
      text: 'The payment reminder has been successfully resent.',
    });
  }

  protected paymentRetried(result: Payment.RetryPaymentResponse) {
    if (this.payment) {
      this.payment.status = result.status;
    }

    this.refresh.emit();
  }

  protected collectionLabel(payment: Payment.Model) {
    const status = payment.status.replace(/_/g, ' ').toLowerCase();
    return Payment.collectionStatusLabel(payment) ?? status.charAt(0).toUpperCase() + status.slice(1);
  }

  /** An outstanding Pay Later balance is charged through the gateway assigned at checkout, so it is only offered when there is one. */
  canCollectPayLater$ = combineLatest([this.payment$, this.canRetryBillingInfo$]).pipe(
    map(
      ([payment, canRetry]) =>
        !!payment &&
        !!canRetry &&
        payment.collectionMode === Payment.CollectionMode.PAY_LATER &&
        [Payment.Status.PENDING, Payment.Status.FAILED].includes(payment.status) &&
        !!payment.gatewayInstanceId
    )
  );

  /** A declined attempt leaves the payment FAILED server-side; reload so the view reflects it. */
  protected paymentAttemptFailed() {
    this.refresh.emit();
  }
}
